import {
  BulkAddManagerUsersToCustomerSuccess,
  ManagerCustomersActions,
  ManagerCustomersActionTypes
} from '@appStore/managers/actions/manager-customers.actions';
import {
  CustomerManagerCompany,
  ManagerCustomerUser,
  ManagerCustomerMeteringPoint,
  ManagerJobLockStatus
} from '@model';

export interface State {
  loading: boolean;
  loaded: boolean;
  meteringPointsLoading: boolean;
  meteringPointsLoaded: boolean;
  data: ManagerCustomerUser[];
  selected: CustomerManagerCompany;

  selectedCustomers: string[];
  selectedCustomersUsers: ManagerCustomerUser[];
  selectedCustomersUsersLoading: boolean;
  selectedCustomersUsersLoaded: boolean;
  
  meteringPoints: ManagerCustomerMeteringPoint[];
  added: ManagerCustomerUser;
  usersAdding: boolean;
  usersAdded: boolean;
  userUpdated: boolean;
  userUpdating: boolean;
  userSelected: ManagerCustomerUser;
  userRemoving: boolean;
  userRemoved: boolean;
  customerRemoving: boolean;
  customerRemoved: boolean;
  customersUpdating: boolean;
  customersUpdated: boolean;
  bulkLockStatus: ManagerJobLockStatus;
  bulkLockLoading: boolean;
  bulkLockLoaded: boolean;
}

export const initialState: State = {
  loading: false,
  loaded: false,
  meteringPointsLoading: false,
  meteringPointsLoaded: false,
  data: [],
  selected: null,
  selectedCustomers: [],
  selectedCustomersUsers: [],
  selectedCustomersUsersLoading: false,
  selectedCustomersUsersLoaded: false,
  meteringPoints: [],
  added: null,
  usersAdding: false,
  usersAdded: false,
  userUpdated: false,
  userUpdating: false,
  userSelected: null,
  userRemoving: false,
  userRemoved: false,
  customerRemoving: false,
  customerRemoved: false,
  customersUpdating: false,
  customersUpdated: false,
  bulkLockStatus: null,
  bulkLockLoading: false,
  bulkLockLoaded: false
};

export function reducer(state = initialState, action: ManagerCustomersActions): State {
  switch (action.type) {
    case ManagerCustomersActionTypes.ManagerCustomerSelect:
      if (state.selected && action.payload && action.payload.id === state.selected.id) {
        return state;
      }
      return { ...state, selected: action.payload, data: [], loaded: false, meteringPoints: [] };

    case ManagerCustomersActionTypes.ManagerCustomersSelection:
          return {
            ...state,
            selectedCustomers: [...action.payload]
          };

    case ManagerCustomersActionTypes.ManagerCustomerUsersLoadData:
      return { ...state, loading: true, loaded: false };
    case ManagerCustomersActionTypes.ManagerCustomerUsersLoadSuccess:
      return { ...state, data: action.payload, loading: false, loaded: true };
    case ManagerCustomersActionTypes.ManagerCustomerUsersLoadFail:
      return { ...state, data: [], loading: false, loaded: false };

    case ManagerCustomersActionTypes.ManagerCustomersUsersLoadData:
      return { ...state, selectedCustomersUsersLoading: true, selectedCustomersUsersLoaded: false };
    case ManagerCustomersActionTypes.ManagerCustomersUsersLoadSuccess:
      return { ...state, selectedCustomersUsers: action.payload, selectedCustomersUsersLoading: false, selectedCustomersUsersLoaded: true };
    case ManagerCustomersActionTypes.ManagerCustomersUsersLoadFail:
      return { ...state, selectedCustomersUsers: [], selectedCustomersUsersLoading: false, selectedCustomersUsersLoaded: false };

    case ManagerCustomersActionTypes.ManagerCustomerMeteringPointsLoad:
      return { ...state, meteringPointsLoading: true, meteringPointsLoaded: false };
    case ManagerCustomersActionTypes.ManagerCustomerMeteringPointsLoadSuccess:
      return { ...state, meteringPoints: action.payload, meteringPointsLoading: false, meteringPointsLoaded: true };
    case ManagerCustomersActionTypes.ManagerCustomerMeteringPointsLoadFail:
      return { ...state, meteringPoints: [], meteringPointsLoading: false, meteringPointsLoaded: false };

    case ManagerCustomersActionTypes.BulkAddManagerUsersToCustomer:
      return { ...state, usersAdding: true, usersAdded: false };
    case ManagerCustomersActionTypes.BulkAddManagerUsersToCustomerSuccess:
      const addedData = action.payload.filter(x => state.data.every(y => y.userId !== x.userId));
      return {
        ...state,
        usersAdding: false,
        usersAdded: true,
        data: action.payload === null ? [] : [...state.data, ...addedData]
      };
    case ManagerCustomersActionTypes.BulkAddManagerUsersToCustomerFail:
      return { ...state, usersAdding: false, usersAdded: false, data: [] };

    // Lock for BulkUpdate - Customers from ManagerUser
    case ManagerCustomersActionTypes.LockManagerCustomerUsersForUpdate:
      return { ...state, bulkLockLoading: true, bulkLockLoaded: false };

    case ManagerCustomersActionTypes.LockManagerCustomerUsersForUpdateSuccess:
      return { ...state, bulkLockLoading: false, bulkLockLoaded: true, bulkLockStatus: action.payload };

    case ManagerCustomersActionTypes.LockManagerCustomerUsersForUpdateFail:
      return { ...state, bulkLockLoading: false, bulkLockLoaded: false, bulkLockStatus: null };

    // Select
    case ManagerCustomersActionTypes.ManagerCustomerUserSelect:
      return { ...state, userSelected: action.payload };

    case ManagerCustomersActionTypes.ManagerCustomerUserRemove:
      return { ...state, userRemoving: true, userRemoved: false };
    case ManagerCustomersActionTypes.ManagerCustomerUserRemoveSuccess:
      const removedUser = action.payload;
      return {
        ...state,
        userRemoving: false,
        userRemoved: true,
        data: state.data.filter(item => item.userId !== removedUser.userId)
      };
    case ManagerCustomersActionTypes.ManagerCustomerUserRemoveFail:
      return { ...state, userRemoving: false, userRemoved: false };

    case ManagerCustomersActionTypes.UpdateManagerCustomerUser:
      return { ...state, userUpdating: true, userUpdated: false };
    case ManagerCustomersActionTypes.UpdateManagerCustomerUserSuccess:
      const updatedItem = action.payload;
      return {
        ...state,
        userUpdating: false,
        userUpdated: true,
        data: state.data.map(item =>
          item.userId === updatedItem.userId &&
          item.managerCompanyId === updatedItem.managerCompanyId &&
          item.customerId === updatedItem.customerId
            ? updatedItem
            : item
        )
      };
    case ManagerCustomersActionTypes.UpdateManagerCustomerUserFail:
      return { ...state, userUpdating: false, userUpdated: false };

    case ManagerCustomersActionTypes.ManagerCustomerRemove:
      return { ...state, customerRemoving: true, customerRemoved: false };
    case ManagerCustomersActionTypes.ManagerCustomerRemoveSuccess:
      const removedItem = action.payload;
      return {
        ...state,
        customerRemoving: false,
        customerRemoved: true,
        data: state.data.filter(
          item => item.managerCompanyId !== removedItem.managerCompanyId || item.customerId !== removedItem.customerId
        )
      };
    case ManagerCustomersActionTypes.ManagerCustomerRemoveFail:
      return { ...state, customerRemoving: false, customerRemoved: false };
  }
  return state;
}

export const getManagerCustomerSelected = (state: State) => state.selected;
export const getManagerCustomersSelected = (state: State) => state.selectedCustomers;

export const getManagerCustomerUsers = (state: State) => state.data;
export const getManagerCustomerUsersLoading = (state: State) => state.loading;
export const getManagerCustomerUsersLoaded = (state: State) => state.loaded;

export const getManagerCustomersUsers = (state: State) => state.selectedCustomersUsers;
export const getManagerCustomersUsersLoading = (state: State) => state.selectedCustomersUsersLoading;
export const getManagerCustomersUsersLoaded = (state: State) => state.selectedCustomersUsersLoaded;

export const getManagerCustomerMeteringPoints = (state: State) => state.meteringPoints;
export const getManagerCustomerMeteringPointsLoading = (state: State) => state.meteringPointsLoading;
export const getManagerCustomerMeteringPointsLoaded = (state: State) => state.meteringPointsLoaded;

export const getManagerUsersToCustomerAdded = (state: State) => state.usersAdded;

export const getManagerCustomerUserSelected = (state: State) => state.userSelected;

export const getManagerCustomerUserUpdated = (state: State) => state.userUpdated;
export const getCustomersForManagerUserUpdated = (state: State) => state.customersUpdated;

export const getLockManagerCustomerUsersForUpdateLoaded = (state: State) => state.bulkLockLoaded;
export const getLockManagerCustomerUsersForUpdate = (state: State) => state.bulkLockStatus;

export const getManagerCustomerRemoved = (state: State) => state.customerRemoved;
export const getManagerCustomerUserRemoved = (state: State) => state.userRemoved;
